import React, {ReactNode, useMemo, useRef} from 'react';
import {HeadTaggerContextValue, HeadTagSetterContext, PageMetaData, ScriptTagData} from './HeadTagSetterContext';

const setMetaTagContent = (name: string, content = '') => {
	const node = (document.head.querySelector(
		`meta[name="${name}"], meta[property="${name}"]`,
	) as HTMLMetaElement) || null;

	if (node) {
		node.content = content;
	}
};

interface BrowserSeoTagsProviderProps {
	children: ReactNode;
}

export const HeadTaggerBrowserProvider = (props: BrowserSeoTagsProviderProps) => {
	const {children} = props;

	const actualPageMeta = useRef<PageMetaData>({});
	const injectedScripts = useRef<string[]>([]);

	const value = useMemo<HeadTaggerContextValue>(
		() => ({
			setPageMeta(newPageMeta: PageMetaData) {
				for (const key of Object.keys(newPageMeta)) {
					const propKey = key as keyof PageMetaData;

					// Ignore not changed properties
					if (newPageMeta[propKey] === actualPageMeta.current[propKey]) {
						continue;
					}

					switch (propKey) {
						case 'title':
							document.title = newPageMeta[propKey] || '';
							setMetaTagContent('title', newPageMeta[propKey]);
							setMetaTagContent('og:title', newPageMeta[propKey]);
							break;

						case 'description':
							setMetaTagContent('description', newPageMeta[propKey]);
							break;

						case 'descriptionOg':
							setMetaTagContent('og:description', newPageMeta[propKey]);
							break;

						case 'keywords':
							setMetaTagContent('keywords', newPageMeta[propKey]);
							break;

						case 'canonicalUrl': {
							const node = document.head.querySelector(`link[rel="canonical"]`) as HTMLLinkElement;
							if (node) {
								node.href = newPageMeta[propKey] || '';
							}
						}

						// Other tag can be ignored because search systems do not index runtime
					}
				}
			},
			addScript(newScript: ScriptTagData) {
				// Ignore when script was injected before
				const cacheId = 'src' in newScript ? newScript.src : newScript.id;
				if (injectedScripts.current.includes(cacheId)) {
					return;
				}

				injectedScripts.current.push(cacheId);

				// Ignore when the script was injected before but outside this module (e.g. by server side rendering or manually)
				if (
					document.head.querySelector(
						'src' in newScript ? `script[src="${newScript.src}"]` : `#${newScript.id}`,
					)
				) {
					return;
				}

				const node = document.createElement('script');
				if ('src' in newScript) {
					node.async = !!newScript.async;
					node.defer = !!newScript.defer;
					node.src = newScript.src;
				} else {
					node.innerHTML = newScript.content;
					node.id = newScript.id;
				}
				document.head.appendChild(node);
			},
			removeScript(scriptData: ScriptTagData) {
				const cacheId = 'src' in scriptData ? scriptData.src : scriptData.id;
				const index = injectedScripts.current.indexOf(cacheId);
				if (index === -1) {
					return;
				}

				injectedScripts.current.splice(index, 1);
				const node = document.head.querySelector(
					'src' in scriptData ? `script[src="${scriptData.src}"]` : `#${scriptData.id}`,
				);
				if (node) {
					node.parentNode!.removeChild(node);
				}
			},
		}),
		[],
	);

	return <HeadTagSetterContext.Provider value={value}>{children}</HeadTagSetterContext.Provider>;
};

HeadTaggerBrowserProvider.displayName = 'BrowserSeoTagsProvider';
